/*
 * Forgot Password — step 1 of password recovery.
 *
 * User enters their username or email; we POST /auth/forgot-password and
 * always navigate to /app/reset-password regardless of whether the account
 * exists (the API mirrors this — returns success in both cases to prevent
 * user enumeration). The identifier rides along as router state so the
 * ResetPassword screen can use it in the reset call.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';

import { api } from '@/lib/api';
import { BrandWordmark } from '@/components/BrandWordmark';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const clean = identifier.trim();
    if (!clean) {
      setError('Please enter your username or email.');
      return;
    }

    setLoading(true);
    try {
      await api.forgotPassword(clean);
      // Always forward — don't reveal whether the account exists.
      navigate('/app/reset-password', {
        state: { identifier: clean },
        replace: false,
      });
    } catch (err: any) {
      // Network/transport failure only; the API itself returns success even
      // for unknown users. So if we land here it's truly a network error.
      setError(err?.message ?? "Couldn't send reset code. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <Link to="/" className="auth-brand">
          <BrandWordmark className="brand-wordmark" />
        </Link>

        <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 12px' }}>
          <KeyRound width={36} height={36} color="#22c55e" strokeWidth={1.75} />
        </div>

        <h1 className="auth-title">Reset password</h1>
        <p className="auth-sub">
          Enter your username or email and we'll send a 6-digit code to the email on file.
        </p>

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <label className="auth-label">
            <span>USERNAME OR EMAIL</span>
            <input
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Enter your username or email"
              className="auth-input"
              disabled={loading}
              autoFocus
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-cta" disabled={loading}>
            {loading ? 'Sending…' : 'Send reset code →'}
          </button>

          <div className="auth-footer">
            <Link to="/app/login" className="auth-footer-link">
              ← Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
